import type React from 'react'
import {useMemo} from 'react'
import {useQueries} from '@github-ui/react-query'
import type {SearchResultsType, SearchResults} from '../types/blackbird-types'
import {getExpandedQuery, parseString} from '@github-ui/blackbird-query-parsing/parsing'
import {getCustomScopesFromParam} from './use-navigate-to-query'
import {CountMode} from '@github-ui/count'
import {reactFetch} from '@github-ui/verified-fetch'

export interface SearchKind {
  name: SearchResultsType
  readableName: string
  readableNamePlural: string
  icon: React.FunctionComponent
}
interface ResultCount {
  mode: CountMode
  total: number
  final: boolean
  error: boolean
}

export type SearchKindCount = SearchKind & ResultCount

const PENDING_COUNT: ResultCount = {total: 0, mode: CountMode.Exact, final: false, error: false}
const RESOLVED_ZERO_COUNT: ResultCount = {total: 0, mode: CountMode.Exact, final: true, error: false}
const ERROR_COUNT: ResultCount = {total: 0, mode: CountMode.Exact, final: true, error: true}

/**
 * A count is a pure function of the request that produces it, so the key is built from what
 * actually goes over the wire — which differs by kind.
 *
 * `code` hits Blackbird, which understands `saved:` qualifiers natively, so it is keyed by the
 * raw `(query, scopes)` pair it sends. Every other kind hits the legacy count endpoint, which
 * does not, so it is keyed by the *expanded* query that scopes have already been substituted
 * into. That distinction matters: for the overwhelmingly common query containing no `saved:`
 * qualifier, expansion is a no-op, so editing an unrelated saved search must not invalidate
 * anything.
 *
 * `searchType` is deliberately absent from both: switching tabs doesn't change what any count
 * means, so it must not invalidate one.
 */
function countKey({
  type,
  query,
  scopes,
  expandedQuery,
}: {
  type: SearchResultsType
  query: string
  scopes: string | null
  expandedQuery: string
}) {
  if (type === 'code') {
    return ['blackbird-search', 'result-count', 'code', query, scopes ?? ''] as const
  }
  return ['blackbird-search', 'result-count', type, expandedQuery] as const
}

/**
 * Must be greater than zero. Every kind's query stays mounted regardless of which tab is active,
 * so the client's `staleTime: 0` default only bites on mount — where it would immediately refetch
 * the entry `initialData` just seeded from the payload, costing a request for a count we already
 * have exactly.
 *
 * Beyond that floor it only governs remounts, so it's kept short. `gcTime` (5min, the default)
 * outlives it, so returning to a recent search renders its counts from cache immediately and
 * revalidates in the background. `isLoading` is false whenever data is present, so that
 * background refetch can't flash the sidebar — which is what makes a short window safe here.
 */
const COUNT_STALE_TIME = 30 * 1000

// NB: Blackbird's CountRPC counts unique "documents", but we display total "files" based on
// counting the search results. If the existing count of total files is higher, keep that number
// to avoid confusion.
function mergeBlackbirdCount(existing: ResultCount, blackbirdCount: ResultCount): ResultCount {
  return existing.total > blackbirdCount.total ? {...blackbirdCount, total: existing.total} : blackbirdCount
}

function isLimitReachedCodePayload(searchType: SearchResultsType, payload: SearchResults): boolean {
  return searchType === 'code' && payload.type === 'code' && !!payload.metadata?.limit_reached
}

function seedFromPayload(searchType: SearchResultsType, payload: SearchResults): ResultCount {
  if (isLimitReachedCodePayload(searchType, payload)) {
    // Set a lower bound estimate while we get the real count
    return {total: payload.result_count, mode: CountMode.LowerBound, final: false, error: false}
  }
  return {total: payload.result_count, mode: CountMode.Exact, final: true, error: false}
}

function expandQuery(query: string, scopes: string | null): string {
  if (!scopes) return query
  const customScopes = getCustomScopesFromParam(scopes)
  if (customScopes.length === 0) return query
  return getExpandedQuery(query, customScopes, parseString(query))
}

export const useSearchResultCounts = ({
  searchType,
  kinds,
  query,
  scopes,
  payload,
}: {
  searchType: SearchResultsType
  kinds: SearchKind[]
  query: string | null
  scopes: string | null
  payload: SearchResults | null
}): [boolean, SearchKindCount[], ResultCount] => {
  const enabled = !!query && !!payload
  const loggedIn = !!payload?.logged_in
  // Expansion parses the query, so memoize it rather than repeating that on every render.
  const expandedQuery = useMemo(() => expandQuery(query ?? '', scopes), [query, scopes])

  const results = useQueries({
    queries: kinds.map(kind => {
      // Each kind closes over exactly the inputs its own key contains, so a cached entry can
      // never be read back for a request that would have differed.
      const request = {type: kind.name, query: query ?? '', scopes, expandedQuery}
      return {
        queryKey: countKey(request),
        queryFn: () => fetchCount(request),
        // The Blackbird count endpoint is authenticated, so there is no code count to be had for
        // a logged-out visitor. Leaving the query disabled keeps it visibly unknown rather than
        // resolving it to a misleading zero.
        enabled: enabled && kind.name !== 'ai_resources' && (kind.name !== 'code' || loggedIn),
        staleTime: COUNT_STALE_TIME,
        // The payload already carries an exact count for the tab we're on, so seeding the cache
        // with it saves a request now and — more importantly — leaves a resolved entry behind for
        // when the user tabs away. That entry is what stops the sidebar from re-fetching (and
        // flashing) this kind on the way back.
        //
        // A limit-reached code payload is deliberately excluded: its `result_count` is only a
        // lower bound ("More than N files"), so seeding it would mark the cache fresh and suppress
        // the exact-count request we still owe the user.
        //
        // Gated on `enabled` because `initialData` is applied when the query is *constructed*,
        // regardless of whether it may fetch — without this, a payload arriving before a query
        // does would seed the count under the empty-query key.
        initialData:
          enabled && payload && kind.name === searchType && !isLimitReachedCodePayload(searchType, payload)
            ? () => seedFromPayload(searchType, payload)
            : undefined,
      }
    }),
  })

  const currentIndex = kinds.findIndex(kind => kind.name === searchType)

  // The active tab renders its own payload's count rather than anything from cache: the
  // payload number is exact, while `/search/count` returns rounded approximations ("1.2k+").
  // The exception is a limit-reached code search, where the payload is only a lower bound and
  // the fetched Blackbird count is the better number. While navigation temporarily clears the
  // payload, retain the cached count instead of flashing zero.
  const currentResult = currentIndex >= 0 ? results[currentIndex] : undefined
  let currentCount = payload
    ? seedFromPayload(searchType, payload)
    : currentResult?.isError
      ? ERROR_COUNT
      : (currentResult?.data ?? PENDING_COUNT)
  const currentData = currentResult?.data
  if (payload && isLimitReachedCodePayload(searchType, payload) && currentData && !currentData.error) {
    currentCount = mergeBlackbirdCount(currentCount, currentData)
  }

  const counts: SearchKindCount[] = kinds.map((kind, index) => {
    if (index === currentIndex) return {...kind, ...currentCount}
    if (kind.name === 'ai_resources') return {...kind, ...RESOLVED_ZERO_COUNT}
    const result = results[index]
    if (result?.isError) return {...kind, ...ERROR_COUNT}
    return {...kind, ...(result?.data ?? PENDING_COUNT)}
  })

  // The active tab renders a payload-derived or cached count, so it must never feed the shared
  // spinner flag: every sidebar counter reads this single boolean, and letting a solo refetch
  // for the active tab flip it would flash all the others into loading placeholders.
  // `isLoading` (rather than `isPending`) so a disabled query doesn't pin the flag on forever.
  const isLoading = enabled && kinds.some((kind, index) => index !== currentIndex && !!results[index]?.isLoading)

  return [isLoading, counts, currentCount]
}

function fetchCount({
  type,
  query,
  scopes,
  expandedQuery,
}: {
  type: SearchResultsType
  query: string
  scopes: string | null
  expandedQuery: string
}): Promise<ResultCount> {
  if (type === 'code') return fetchBlackbirdCount({query, scopes: scopes ?? ''})
  return fetchSearchCount({type, expandedQuery})
}

async function fetchBlackbirdCount({query, scopes}: {query: string; scopes: string}): Promise<ResultCount> {
  const url = new URL('/search/blackbird_count', window.location.href)
  url.searchParams.append('saved_searches', scopes)
  url.searchParams.append('q', query)
  const response = await reactFetch(url.toString(), {
    headers: {Accept: 'application/json'},
  })
  const json = await response.json()
  return {total: Number(json.count), mode: json.mode, final: true, error: json.failed}
}

async function fetchSearchCount({
  type,
  expandedQuery,
}: {
  type: SearchResultsType
  expandedQuery: string
}): Promise<ResultCount> {
  // `codelegacy` is the legacy code index behind the same `type=code` endpoint parameter.
  const typeValue = type === 'codelegacy' ? 'code' : type

  let q = expandedQuery
  if (type === 'issues') {
    q += ' is:issue'
  } else if (type === 'pullrequests') {
    q += ' is:pr'
  }

  const url = new URL('/search/count', window.location.href)
  url.searchParams.append('q', q)
  url.searchParams.append('type', typeValue)

  const response = await reactFetch(url.toString(), {
    headers: {Accept: 'application/json'},
  })

  if (!response.ok) {
    throw new Error(`${response.status}`)
  }

  if (response.headers.get('content-type')?.includes('application/json')) {
    const result = (await response.json()) as {total: number; mode: 'exact' | 'lower_bound'}
    return {
      mode: result.mode === 'lower_bound' ? CountMode.LowerBound : CountMode.Exact,
      total: result.total,
      final: true,
      error: false,
    }
  }

  const result = await response.text()
  return extractContent(result)
}

function extractContent(html: string): ResultCount {
  const parsedDocument = new DOMParser().parseFromString(html, 'text/html')
  let text = (parsedDocument.body.textContent || '0').trim().toLowerCase()
  let multiplier = 1
  let isLowerBound = false

  if (text.endsWith('+')) {
    isLowerBound = true
    text = text.slice(0, -1)
  }

  if (text.endsWith('k')) {
    multiplier = 1000
    text = text.slice(0, -1)
  } else if (text?.endsWith('m')) {
    multiplier = 1000000
    text = text.slice(0, -1)
  } else if (text?.endsWith('b')) {
    multiplier = 1000000000
    text = text.slice(0, -1)
  }

  return {
    mode: isLowerBound ? CountMode.LowerBound : CountMode.Exact,
    total: Number(text) * multiplier,
    final: true,
    error: false,
  }
}
